import * as React from "react"
import type { EpicViteContext, renderFunc } from "@skyltmax/epic_vite"
import i18n from "@/common/locales"
import { SentryContext } from "@/common/sentry"
import SentryReact from "@/common/sentry_client"
import ErrorBoundary from "@/common/components/error_boundary"
import { ApolloProviderPreloader } from "@/api/client"
import growthbook from "@/common/growthbook"
import type { SkyltmaxViteContext } from "@/common/vite_context"
import { GrowthBookProvider } from "@/common/growthbook-react"
import { EpicViteCustomContext } from "@/admin/containers/contexts"

const WrappedApp = (
  Component: React.ComponentType<EpicViteContext<any>>,
  context: EpicViteContext<any, SkyltmaxViteContext>
) => {
  return (
    <React.StrictMode>
      <SentryContext.Provider value={SentryReact}>
        <React.Suspense fallback={null}>
          <ErrorBoundary>
            <GrowthBookProvider growthbook={growthbook}>
              <ApolloProviderPreloader {...context.props}>
                <Component {...context} />
              </ApolloProviderPreloader>
            </GrowthBookProvider>
          </ErrorBoundary>
        </React.Suspense>
      </SentryContext.Provider>
    </React.StrictMode>
  )
}

export const appShell = <TProps,>(
  Component: React.ComponentType<EpicViteContext<TProps>>,
  preHook?: () => void
): renderFunc<TProps> => {
  const WrappedRenderFunc: renderFunc<any, SkyltmaxViteContext> = context => {
    if (i18n.language !== context.locale) {
      i18n.changeLanguage(context.locale)
    }

    if (context.customContext?.flags) {
      growthbook.initSync({
        payload: {
          features: {
            ...context.customContext.flags,
            ...(context.customContext.overrides || {}),
          },
        },
      })
    }

    if (context.customContext?.flagsAttributes) {
      growthbook.setAttributes(context.customContext.flagsAttributes)
    }

    if (preHook) {
      preHook()
    }

    return WrappedApp(Component, context)
  }

  return WrappedRenderFunc
}

const WrappedComponent = (Component: React.ComponentType<any>, props?: any, context?: SkyltmaxViteContext) => {
  return (
    <SentryContext.Provider value={SentryReact}>
      <EpicViteCustomContext.Provider value={context || {}}>
        <React.Suspense fallback={null}>
          <ErrorBoundary>
            <GrowthBookProvider growthbook={growthbook}>
              <ApolloProviderPreloader {...props}>
                <Component {...props} />
              </ApolloProviderPreloader>
            </GrowthBookProvider>
          </ErrorBoundary>
        </React.Suspense>
      </EpicViteCustomContext.Provider>
    </SentryContext.Provider>
  )
}

const componentShell = <TProps,>(Component: React.ComponentType<TProps>, preHook?: () => void): renderFunc<TProps> => {
  const WrappedRenderFunc: renderFunc<any, SkyltmaxViteContext> = context => {
    if (i18n.language !== context.locale) {
      i18n.changeLanguage(context.locale)
    }

    if (context.customContext?.flags) {
      growthbook.initSync({
        payload: {
          features: {
            ...context.customContext.flags,
            ...(context.customContext.overrides || {}),
          },
        },
      })
    }

    if (context.customContext?.flagsAttributes) {
      growthbook.setAttributes(context.customContext.flagsAttributes)
    }

    if (preHook) {
      preHook()
    }

    return WrappedComponent(Component, context.props, context.customContext)
  }

  return WrappedRenderFunc
}

export default componentShell
